import { useEffect, useRef, useState } from "react";
import { objectsResolveAddressThunk } from "app/features";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectObjectsState } from "app/data/selectors";

const useObjectsResolvedAddressess = (data: any) => {
  const dispatch = useAppDispatch();
  const { resolvedAddresses } = useAppSelector(selectObjectsState);

  const hasResolvedAddresses = useRef(false);
  const processedDataKey = useRef<string>("");
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(
    new Set()
  );
  const [resolvedAddressLoading, setResolvedAddressLoading] = useState(false);

  useEffect(() => {
    // Create a unique key for the current data to detect changes
    const currentDataKey = data?.length
      ? `${data.length}-${data.map((item: any) => item.uniqueId || item.id).join(",")}`
      : "";

    // Reset the flag if we have completely different data
    if (currentDataKey !== processedDataKey.current) {
      hasResolvedAddresses.current = false;
      processedDataKey.current = currentDataKey;
      setPendingRequests(new Set());
      setResolvedAddressLoading(false);
    }

    if (!hasResolvedAddresses.current && data?.length) {
      const uniqueCoordinates = new Set();
      const newPendingRequests = new Set<string>();

      data.forEach((row: any) => {
        if (
          row.location?.latitude > 0 &&
          row.location?.longitude > 0 &&
          !row.location?.resolvedAddress // Skip API call if address already exists
        ) {
          const locationKey = `${row.location.latitude},${row.location.longitude}`;

          if (!uniqueCoordinates.has(locationKey)) {
            uniqueCoordinates.add(locationKey);
            const existingAddress = resolvedAddresses.find(
              (item) =>
                item.latitude === row.location.latitude &&
                item.longitude === row.location.longitude
            );

            if (!existingAddress) {
              newPendingRequests.add(locationKey);

              // Dispatch the API call and track its completion
              dispatch(
                objectsResolveAddressThunk({
                  latitude: row.location.latitude,
                  longitude: row.location.longitude
                })
              )
                .then(() => {
                  // Remove this request from pending when it completes
                  setPendingRequests((prev) => {
                    const updated = new Set(prev);
                    updated.delete(locationKey);
                    return updated;
                  });
                })
                .catch(() => {
                  // Remove this request from pending even if it fails
                  setPendingRequests((prev) => {
                    const updated = new Set(prev);
                    updated.delete(locationKey);
                    return updated;
                  });
                });
            }
          }
        }
      });

      if (newPendingRequests.size > 0) {
        setPendingRequests(newPendingRequests);
        setResolvedAddressLoading(true);
        hasResolvedAddresses.current = true;
      }
    }
  }, [data, resolvedAddresses, dispatch]);

  // Update loading state based on pending requests
  useEffect(() => {
    if (pendingRequests.size === 0 && resolvedAddressLoading) {
      setResolvedAddressLoading(false);
    }
  }, [pendingRequests.size, resolvedAddressLoading]);

  return { resolvedAddresses, resolvedAddressLoading };
};

export default useObjectsResolvedAddressess;
